import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, BadgeCheck, Star, Plus, Search, Download, Upload, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';
import { ikThumb } from '../lib/imagekit';
import ImportArtistsModal from './ImportArtistsModal';

const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Date Added · Newest' },
  { value: 'created_asc',  label: 'Date Added · Oldest' },
  { value: 'updated_desc', label: 'Date Modified · Newest' },
  { value: 'updated_asc',  label: 'Date Modified · Oldest' },
  { value: 'name_asc',     label: 'Name · A → Z' },
  { value: 'name_desc',    label: 'Name · Z → A' },
  { value: 'category_asc', label: 'Category · A → Z' },
  { value: 'price_asc',    label: 'Price · Low → High' },
  { value: 'price_desc',   label: 'Price · High → Low' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const primaryCategory = (a) => (a.occasions?.[0] || a.specializations?.[0] || '');

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [importOpen, setImportOpen] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    adminApi.list().then(setArtists).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = async (slug, key, current) => {
    try {
      await adminApi.toggleFlags(slug, { [key]: !current });
      setArtists(arr => arr.map(a => a.slug === slug ? { ...a, [key]: !current } : a));
      toast.success(`${key} updated`);
    } catch {
      toast.error('Could not update');
    }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await adminApi.remove(slug);
      setArtists(arr => arr.filter(a => a.slug !== slug));
      toast.success('Artist deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  const exportCsv = async () => {
    try {
      const blob = await adminApi.exportCsvBlob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `findyourmua-artists-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${artists.length} artists`);
    } catch {
      toast.error('Export failed');
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) setImportOpen(f);
  };

  const filtered = useMemo(() => {
    let arr = artists;
    if (q) {
      const needle = q.toLowerCase();
      arr = arr.filter(a => `${a.name} ${a.city} ${(a.specializations || []).join(' ')} ${(a.occasions || []).join(' ')}`.toLowerCase().includes(needle));
    }
    const sorted = [...arr];
    const byStr = (k) => (a, b) => (a[k] || '').toString().localeCompare((b[k] || '').toString());
    const byNum = (k) => (a, b) => (a[k] || 0) - (b[k] || 0);
    switch (sort) {
      case 'created_desc': sorted.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')); break;
      case 'created_asc':  sorted.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')); break;
      case 'updated_desc': sorted.sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '')); break;
      case 'updated_asc':  sorted.sort((a, b) => (a.updated_at || a.created_at || '').localeCompare(b.updated_at || b.created_at || '')); break;
      case 'name_asc':     sorted.sort(byStr('name')); break;
      case 'name_desc':    sorted.sort(byStr('name')).reverse(); break;
      case 'category_asc': sorted.sort((a, b) => primaryCategory(a).localeCompare(primaryCategory(b))); break;
      case 'price_asc':    sorted.sort(byNum('starting_price')); break;
      case 'price_desc':   sorted.sort(byNum('starting_price')).reverse(); break;
      default: break;
    }
    return sorted;
  }, [artists, q, sort]);

  return (
    <div data-testid="admin-artists-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Directory</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight">Artists ({artists.length})</h1>
          <p className="mt-2 text-sm text-neutral-600">Manage all artists — verified, featured, published, dates, sort, CSV import/export.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-[#eaeaea] px-3 py-2">
            <Search className="w-4 h-4 text-neutral-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search…"
              className="bg-transparent outline-none text-sm w-44"
              data-testid="admin-artists-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="admin-artists-sort"
            className="bg-white border border-[#eaeaea] px-3 py-2 text-[11px] uppercase tracking-[0.15em] outline-none"
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="admin-export-csv-btn"
            className="inline-flex items-center gap-2 border border-neutral-300 hover:border-black hover:bg-black hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="admin-import-csv-btn"
            className="inline-flex items-center gap-2 border border-neutral-300 hover:border-black hover:bg-black hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onPickFile} className="hidden" />
          <Link to="/admin/artists/new" data-testid="admin-add-artist-btn" className="inline-flex items-center gap-2 bg-[#eb5381] hover:bg-[#d64570] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.2em]">
            <Plus className="w-4 h-4" /> Add Artist
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[0,1,2].map(i => <div key={i} className="h-16 bg-white animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-10 text-center border border-[#eaeaea]">
          <div className="font-serif text-2xl">No artists found.</div>
          <Link to="/admin/artists/new" className="mt-5 inline-flex items-center gap-2 bg-[#eb5381] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.2em]">
            <Plus className="w-4 h-4" /> Add your first artist
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-[#eaeaea] overflow-x-auto" data-testid="admin-artists-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#fafafa] text-left text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                <th className="p-4 font-normal">Artist</th>
                <th className="p-4 font-normal">City</th>
                <th className="p-4 font-normal">Category</th>
                <th className="p-4 font-normal">From</th>
                <th className="p-4 font-normal">Added</th>
                <th className="p-4 font-normal">Modified</th>
                <th className="p-4 font-normal text-center">Verified</th>
                <th className="p-4 font-normal text-center">Featured</th>
                <th className="p-4 font-normal text-center">Published</th>
                <th className="p-4 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const isPublished = a.published !== false;
                return (
                <tr key={a.slug} className={`border-t border-[#eaeaea] hover:bg-[#fff8ee] ${!isPublished ? 'opacity-60' : ''}`} data-testid={`admin-row-${a.slug}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {a.profile_image && <img src={ikThumb(a.profile_image, 80)} alt="" className="w-10 h-10 object-cover" />}
                      <div>
                        <div className="font-serif text-lg leading-tight">{a.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">{a.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{a.city}</td>
                  <td className="p-4 text-xs text-neutral-600">{primaryCategory(a) || '—'}</td>
                  <td className="p-4">₹{(a.starting_price || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-xs text-neutral-600 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="p-4 text-xs text-neutral-600 whitespace-nowrap">{fmtDate(a.updated_at || a.created_at)}</td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => toggle(a.slug, 'verified', a.verified)}
                      data-testid={`toggle-verified-${a.slug}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border ${a.verified ? 'border-[#eb5381] text-[#eb5381] bg-[#fff8ee]' : 'border-neutral-300 text-neutral-500'}`}
                    >
                      <BadgeCheck className="w-3 h-3" /> {a.verified ? 'Yes' : 'No'}
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => toggle(a.slug, 'featured', a.featured)}
                      data-testid={`toggle-featured-${a.slug}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border ${a.featured ? 'border-[#eb5381] text-[#eb5381] bg-[#fff8ee]' : 'border-neutral-300 text-neutral-500'}`}
                    >
                      <Star className={`w-3 h-3 ${a.featured ? 'fill-[#eb5381]' : ''}`} /> {a.featured ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => toggle(a.slug, 'published', isPublished)}
                      data-testid={`toggle-published-${a.slug}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border ${isPublished ? 'border-[#22c55e] text-[#16a34a] bg-[#f0fdf4]' : 'border-neutral-300 text-neutral-500'}`}
                    >
                      {isPublished ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {isPublished ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link to={`/admin/artists/${a.slug}/edit`} data-testid={`edit-${a.slug}`} className="w-8 h-8 grid place-items-center border border-neutral-300 hover:border-[#eb5381] hover:text-[#eb5381]">
                        <Edit className="w-3.5 h-3.5" />
                      </Link>
                      <button onClick={() => remove(a.slug, a.name)} data-testid={`delete-${a.slug}`} className="w-8 h-8 grid place-items-center border border-neutral-300 hover:border-red-500 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {importOpen && (
        <ImportArtistsModal
          file={importOpen}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
        />
      )}
    </div>
  );
}
